import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LineChart as LineChartIcon, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  PortalMetric,
  PortalMetricSeries,
  portalMentorService,
} from '../../../services/api';
import { Avatar, EmptyState, fa, inputClass } from './MentorUI';

type Aggregate = 'avg' | 'sum' | 'median';
const AGGREGATE_LABELS: Record<Aggregate, string> = {
  avg: 'میانگین',
  sum: 'مجموع',
  median: 'میانه',
};

const formatValue = (value: number | null, metric?: PortalMetric) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const rounded = Math.abs(value) >= 100 ? Math.round(value) : Math.round(value * 100) / 100;
  const base = rounded.toLocaleString('fa-IR');
  switch (metric?.format) {
    case 'CURRENCY':
      return base + ' ' + (metric.unit || 'تومان');
    case 'PERCENT':
      return base + '٪';
    default:
      return metric?.unit ? base + ' ' + metric.unit : base;
  }
};

/** جهت شاخص تعیین می‌کند افزایش خوب است یا بد */
const tone = (change: number | null, metric?: PortalMetric) => {
  if (change === null || change === 0 || metric?.direction === 'NEUTRAL') return 'neutral';
  const good = metric?.direction === 'DOWN' ? change < 0 : change > 0;
  return good ? 'good' : 'bad';
};
const TONE_CLASS: Record<string, string> = {
  good: 'text-emerald-600',
  bad: 'text-red-600',
  neutral: 'text-gray-400',
};

interface Props {
  onError: (err: any, fallback: string) => void;
}

/**
 * روند شاخص‌های تیم منتور.
 *
 * دامنه روی سرور قفل است — این صفحه هیچ پارامتری برای «کدام گروه» نمی‌فرستد و
 * حتی اگر بفرستد، سرور آن را با فهرست شرکت‌کنندگان همین منتور اشتراک می‌گیرد.
 */
export const MentorTrendTab: React.FC<Props> = ({ onError }) => {
  const [metrics, setMetrics] = useState<PortalMetric[]>([]);
  const [metricId, setMetricId] = useState('');
  const [aggregate, setAggregate] = useState<Aggregate>('avg');
  const [onlyApproved, setOnlyApproved] = useState(false);
  const [series, setSeries] = useState<PortalMetricSeries | null>(null);
  const [loading, setLoading] = useState(false);

  const loadMetrics = useCallback(async () => {
    try {
      const list = await portalMentorService.listMetrics();
      const active = list.filter((m) => m.isActive);
      setMetrics(active);
      setMetricId((cur) => cur || active[0]?.id || '');
    } catch (err: any) {
      onError(err, 'دریافت شاخص‌ها انجام نشد.');
    }
  }, [onError]);

  const loadSeries = useCallback(async () => {
    if (!metricId) {
      setSeries(null);
      return;
    }
    setLoading(true);
    try {
      setSeries(await portalMentorService.getMetricSeries({ metricId, onlyApproved }));
    } catch (err: any) {
      onError(err, 'دریافت روند انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, [metricId, onlyApproved, onError]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);
  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  const metric = series?.metric;

  const chartData = useMemo(
    () =>
      (series?.points ?? []).map((p) => ({
        name: p.title.length > 16 ? p.title.slice(0, 16) + '…' : p.title,
        value: p[aggregate],
      })),
    [series, aggregate],
  );

  if (!metrics.length) {
    return (
      <EmptyState
        icon={<LineChartIcon className="h-5 w-5" />}
        title="هنوز شاخصی تعریف نشده"
        body="مدیریت دوره باید شاخص‌های قابل‌پیگیری را تعریف کند و فیلدهای عددی تسک‌ها را به آن‌ها وصل کند. بعد از آن روند تیم شما اینجا نمایش داده می‌شود."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <div className="grid gap-3 md:grid-cols-3">
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-gray-600">شاخص</span>
            <select
              value={metricId}
              onChange={(e) => setMetricId(e.target.value)}
              className={inputClass}
            >
              {metrics.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.title}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-gray-600">نقطهٔ نمودار</span>
            <select
              value={aggregate}
              onChange={(e) => setAggregate(e.target.value as Aggregate)}
              className={inputClass}
            >
              {(Object.keys(AGGREGATE_LABELS) as Aggregate[]).map((a) => (
                <option key={a} value={a}>
                  {AGGREGATE_LABELS[a]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-end gap-1.5 text-xs font-bold text-gray-700">
            <input
              type="checkbox"
              checked={onlyApproved}
              onChange={(e) => setOnlyApproved(e.target.checked)}
              className="mb-2.5"
            />
            <span className="mb-2">فقط پاسخ‌های تأییدشده</span>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white py-16 text-center text-sm text-gray-500">
          در حال محاسبه…
        </div>
      ) : !series || !series.points.length ? (
        <EmptyState
          icon={<LineChartIcon className="h-5 w-5" />}
          title="داده‌ای برای این شاخص ثبت نشده"
          body="وقتی شرکت‌کنندگان شما تسکی را که فیلدش به این شاخص وصل است ارسال کنند، نمودار اینجا ساخته می‌شود."
        />
      ) : (
        <>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="mb-3 text-sm font-bold text-gray-800">
              روند {AGGREGATE_LABELS[aggregate]} تیم شما · {metric?.title}
            </p>
            <div className="h-64 w-full" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={70} />
                  <Tooltip formatter={(v: any) => formatValue(Number(v), metric)} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name={metric?.title}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white">
            <p className="border-b border-gray-100 px-4 py-3 text-sm font-bold text-gray-800">
              وضعیت هر شرکت‌کننده
            </p>
            <div className="divide-y divide-gray-100">
              {series.participants.map((p) => {
                const t = tone(p.change, metric);
                const Icon = t === 'good' ? TrendingUp : t === 'bad' ? TrendingDown : Minus;
                const filled = p.values.filter((v) => v !== null).length;
                const missing = series.points.length - filled;
                return (
                  <div key={p.participantId} className="flex items-center gap-3 px-4 py-3">
                    <Avatar user={{ firstName: p.name }} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-gray-900">{p.name}</p>
                      <p className="text-[11px] text-gray-400">
                        {fa(filled)} از {fa(series.points.length)} ثبت
                        {missing > 0 ? ' · ' + fa(missing) + ' مورد جاافتاده' : ''}
                      </p>
                    </div>
                    <div className="shrink-0 text-left">
                      <p className="text-sm font-black text-gray-900">
                        {formatValue(p.latest, metric)}
                      </p>
                      <p className={'flex items-center justify-end gap-1 text-[11px] font-bold ' + TONE_CLASS[t]}>
                        <Icon className="h-3 w-3" />
                        {p.changePercent !== null
                          ? (p.changePercent > 0 ? '+' : '') +
                            Math.round(p.changePercent).toLocaleString('fa-IR') +
                            '٪'
                          : 'بدون مقایسه'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MentorTrendTab;
